import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MessageEditContext, MessageProvider, useMessageEditContext } from './MessageEditProvider';
import Bread from '../../components/layout/BreadCrumbs';
import Title from '../../components/layout/Title';
import { Box, Button, Checkbox, Option, Select, Typography } from '@mui/joy';
import { useLanguageContext } from '../../context/LanguageContext';
import { Loading } from '../../components/loading';
import { ViewField } from '../../components/ViewField';
import { utcToLocal } from '../../utils/moment_utils';
import { useInfo } from '../../utils/hooks/useInfo';
import { UserMessagesApi } from '../../utils/crud/user_message_api';
import { rethrow } from '../../utils/crud';

const crud = new UserMessagesApi();

const IsReadCheckBox = ({ is_read, setIsRead }) => {
    const { id } = useParams();
    const [disabled, setDisabled] = useState(false);
    const info = useInfo();

    const update = async (new_status) => {
        try {
            setDisabled(true);
            await crud.setReadStatus(id, new_status).then(rethrow);
            setIsRead(new_status);
        } catch (e) {
            info(`Can't save read status`, 'danger');
            console.error(e);
        } finally {
            setDisabled(false);
        }
    };

    const onClick = async (e) => {
        await update(!is_read);
    };

    useEffect(() => {
        update(true);
    }, []);

    return <Checkbox disabled={disabled} checked={!!is_read} onChange={onClick}></Checkbox>;
};

const Form = () => {
    const { t } = useLanguageContext();
    let {
        user_id,
        session_id,
        user_name,
        email,
        phone,
        message,
        is_read,
        setIsRead,
        created_at,
    } = useMessageEditContext();

    return (
        <Box
            flexDirection="column"
            display="flex"
            sx={{
                gap: { xs: 2, md: 3 },
                margin: { xs: '0 auto', md: '0' },
                px: { xs: 2, md: 4 },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    gap: { xs: 2, md: 3 },
                }}
            >
                <ViewField 
                    name={t('User ID')} 
                    value={user_id} 
                    sx={{ 
                        flex: { xs: '1 1 100%', sm: '1 1 auto' },
                        minWidth: { xs: '100%', sm: '150px' }
                    }} 
                />
                <ViewField 
                    name={t('Session ID')} 
                    value={session_id} 
                    sx={{ 
                        flex: { xs: '1 1 100%', sm: '1 1 auto' }, 
                        minWidth: { xs: '100%', sm: '150px' } 
                    }} 
                />
                <ViewField 
                    name={t('User name')} 
                    value={user_name} 
                    sx={{ 
                        flex: { xs: '1 1 100%', sm: '1 1 auto' }, 
                        minWidth: { xs: '100%', sm: '150px' } 
                    }} 
                />
                <ViewField 
                    name={t('Email')} 
                    value={email} 
                    sx={{ 
                        flex: { xs: '1 1 100%', sm: '1 1 auto' }, 
                        minWidth: { xs: '100%', sm: '150px' } 
                    }} 
                />
                <ViewField 
                    name={t('Phone')} 
                    value={phone} 
                    sx={{ 
                        flex: { xs: '1 1 100%', sm: '1 1 auto' }, 
                        minWidth: { xs: '100%', sm: '150px' } 
                    }} 
                />
                <ViewField
                    name={t('Created At')}
                    value={utcToLocal(created_at)}
                    sx={{ 
                        flex: { xs: '1 1 100%', sm: '1 1 auto' }, 
                        minWidth: { xs: '100%', sm: '150px' } 
                    }} 
                />
                <ViewField
                    name={t('Read')}
                    value={<IsReadCheckBox is_read={is_read} setIsRead={setIsRead} />}
                    sx={{ 
                        flex: { xs: '1 1 100%', sm: '1 1 auto' }, 
                        minWidth: { xs: '100%', sm: '150px' } 
                    }} 
                />
            </Box>

            {/* Поле с сообщением */}
            <Box 
                sx={{
                    width: '100%',
                    mt: { xs: 2, md: 3 },
                    whiteSpace: 'pre-line'
                }}
            >
                <ViewField 
                    name={t('Message')} 
                    value={message} 
                    sx={{ whiteSpace: 'pre-line' }} 
                />
            </Box>
        </Box>
    );
};

const _Edit = () => {
    const { id } = useParams();
    const { t } = useLanguageContext();
    let { loading } = useMessageEditContext();
    let title = t('Message #') + id;

    if (loading) return <Loading />;

    return (
        <>
            <Bread
                path={[
                    { name: t('User messages'), path: '/messages' },
                    { name: title },
                ]}
                sx={{
                    mb: { xs: 2, md: 3 },
                    px: { xs: 2, md: 4 },
                }}
            />
            <Title>
                <Typography 
                    level="h2" 
                    component="h1"
                    sx={{
                        fontSize: { xs: '1.5rem', md: '2rem' },
                        textAlign: { xs: 'center', md: 'left' },
                        px: { xs: 2, md: 4 },
                    }}
                >
                    {title}
                </Typography>
            </Title>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    gap: { xs: 3, md: 5 },
                    margin: { md: '0 auto', lg: '0' }, 
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                }}
            >
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 2, md: 3 },
                    }}
                >
                    <Form />
                </Box>
            </Box>
        </>
    );
};

const MessageEdit = () => {
    const { id } = useParams();
    return (
        <MessageProvider id={id}>
            <_Edit />
        </MessageProvider>
    );
};

export default MessageEdit;